import { inject, Injectable, OnDestroy } from '@angular/core'
import { TranslocoService } from '@jsverse/transloco'
import { firstValueFrom, Subject, Subscription } from 'rxjs'
import { ToastMessage, ToastType } from '../_components/toast/toast.component'
import { Fault } from '../_models/class/fault'
import { FaultCode } from '../_models/enum/fault-code'

export interface Toast {
  type: ToastType
  duration: number
  progress: number
  entity: string | null
  toastMessage: ToastMessage
  translationParams?: Record<string, string | null>
}

@Injectable({
  providedIn: 'root',
})
export class ToastService implements OnDestroy {
  private readonly transloco = inject(TranslocoService)
  private readonly toastSubject = new Subject<Toast>()
  private langChangeSubscription?: Subscription
  private toastMessagesLoadedForLang?: string

  toastState$ = this.toastSubject.asObservable()

  constructor() {
    void this.preloadToastMessages()
    this.langChangeSubscription = this.transloco.langChanges$.subscribe(lang => {
      this.toastMessagesLoadedForLang = undefined
      void this.preloadToastMessages(lang)
    })
  }

  /** toastMessages is a lazy scope; load before showing so text is not delayed behind the shell. */
  private async preloadToastMessages(lang?: string): Promise<void> {
    const activeLang = lang ?? this.transloco.getActiveLang()
    if (this.toastMessagesLoadedForLang === activeLang) {
      return
    }
    try {
      await firstValueFrom(
        this.transloco.load(`toastMessages/${activeLang}`)
      )
      this.toastMessagesLoadedForLang = activeLang
    } catch {
      // Still show toast; template may fall back to keys until load retries.
    }
  }

  private emitToast(toast: Toast): void {
    void this.preloadToastMessages().then(() => {
      this.toastSubject.next(toast)
    })
  }

  triggerToast(
    type: ToastType,
    toastMessage: ToastMessage,
    entity: string | null = null,
    duration: number = 3000,
    translationParams?: Record<string, string | null>
  ) {
    this.emitToast({
      type,
      duration,
      entity,
      progress: 100,
      toastMessage: toastMessage,
      translationParams,
    })
  }

  triggerFaultToast(
    fault: Fault,
    entity: string | null = null,
    type: ToastType = ToastType.Error,
    duration: number = 3000
  ) {
    this.emitToast({
      type,
      duration,
      entity,
      progress: 100,
      toastMessage: this.getToastMessageFromFault(fault),
    })
  }

  getToastMessageFromFault(fault: Fault): ToastMessage {
    switch (fault.code) {
      case FaultCode.Unspecified:
        return ToastMessage.Unspecified_Error
      case FaultCode.Set:
        return ToastMessage.Set_Error
      case FaultCode.ApiLevelNoLongerSupported:
        return ToastMessage.ApiLevelNoLongerSupported_Error
      case FaultCode.Throttling:
        return ToastMessage.Throttling_Error
      case FaultCode.Duplicate:
        return ToastMessage.Duplicate_Error
      case FaultCode.WrongPassword:
        return ToastMessage.WrongPassword_Error
      case FaultCode.NotFound:
        return ToastMessage.NotFound_Error
      case FaultCode.Unauthorized:
        return ToastMessage.Unauthorized_Error
      case FaultCode.Forbidden:
        return ToastMessage.Forbidden_Error
      case FaultCode.IllegalArgument:
        return ToastMessage.IllegalArgument_Error
      case FaultCode.IllegalState:
        return ToastMessage.IllegalState_Error
      case FaultCode.Ineligible:
        return ToastMessage.Ineligible_Error
      case FaultCode.NoData:
        return ToastMessage.NoData_Error
      case FaultCode.NoExtra:
        return ToastMessage.NoExtra_Error
      case FaultCode.Depleted:
        return ToastMessage.Depleted_Error
      case FaultCode.PreconditionFailed:
        return ToastMessage.PreconditionFailed_Error
      case FaultCode.FileError:
        return ToastMessage.FileError_Error
      case FaultCode.Expired:
        return ToastMessage.Expired_Error
      default:
        return ToastMessage.Update_Error
    }
  }

  ngOnDestroy() {
    this.langChangeSubscription?.unsubscribe()
    this.toastSubject.complete()
  }
}
